/**
 * SEO URL builders — generate canonical paths for all page types.
 * Every function produces a clean, slugified URL path.
 *
 * Usage:
 *   buildCountryPath('Italy')          → '/italy'
 *   buildTourPath('italy', 'rome', ...) → '/italy/rome/tour/...'
 *   toAbsoluteUrl('/italy')             → 'https://mondivago.com/italy'
 */

export const SITE_URL = String(import.meta.env.VITE_SITE_URL ?? 'https://mondivago.com').replace(/\/$/, '');

/**
 * Converts any string to a URL-safe slug.
 * Handles unicode characters (è → e), removes special chars, collapses dashes.
 * Falls back to `fallback` if the result is empty.
 */
export function slugifySegment(value: string, fallback = 'global') {
  return (
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || fallback
  );
}

/** Strips trailing slash and resolves empty path to '/' */
function normalizePath(pathname: string) {
  if (!pathname || pathname === '/') return '/';
  return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}

/** Converts a relative path to an absolute URL using SITE_URL */
export function toAbsoluteUrl(pathname: string) {
  return `${SITE_URL}${normalizePath(pathname)}`;
}

/** /:country slug */
export function buildCountryPath(countrySlug: string) {
  return `/${slugifySegment(countrySlug)}`;
}

/** /:country/:city slug */
export function buildCityPath(countrySlug: string, citySlug: string) {
  return `/${slugifySegment(countrySlug)}/${slugifySegment(citySlug)}`;
}

/** /:country/:city/category/:category slug */
export function buildCategoryPath(countrySlug: string, citySlug: string, categorySlug: string) {
  return `/${slugifySegment(countrySlug)}/${slugifySegment(citySlug)}/category/${slugifySegment(categorySlug)}`;
}

/** /:country/category/:category slug */
export function buildCountryCategoryPath(countrySlug: string, categorySlug: string) {
  return `/${slugifySegment(countrySlug)}/category/${slugifySegment(categorySlug)}`;
}

/** /:country/:city/category/:category slug */
export function buildCityCategoryPath(countrySlug: string, citySlug: string, categorySlug: string) {
  return `/${slugifySegment(countrySlug)}/${slugifySegment(citySlug)}/category/${slugifySegment(categorySlug)}`;
}

/**
 * /:country/:city/tour/:tourSlug
 * tourSlug should already contain the shortId (e.g. "gladiator-museum-a7b29f1c")
 */
export function buildTourPath(countrySlug: string, citySlug: string, tourSlug: string, _shortId?: string) {
  return `/${slugifySegment(countrySlug)}/${slugifySegment(citySlug)}/tour/${tourSlug}`;
}

/** /:country/:city/attractions/:attraction slug */
export function buildAttractionPath(countrySlug: string, citySlug: string, attractionSlug: string) {
  return `/${slugifySegment(countrySlug)}/${slugifySegment(citySlug)}/attractions/${slugifySegment(attractionSlug)}`;
}

/**
 * /supplier/:slug
 * Builds a supplier URL from a company name and optional short ID.
 * Example: buildSupplierPath('Mario Rossi', 'abc123') → '/supplier/mario-rossi-abc123'
 */
export function buildSupplierPath(name: string, shortId?: string) {
  const slug = shortId ? `${slugifySegment(name)}-${shortId}` : slugifySegment(name);
  return `/supplier/${slug}`;
}

/**
 * Build a clean tour slug with short ID.
 * Example: "gladiator-museum-a7b29f1c"
 */
export const buildTourSlug = (title: string, shortId?: string) => {
  const slug = slugifySegment(title, 'tour');
  return shortId ? `${slug}-${shortId}` : slug;
};

/** Full canonical URL for a path (e.g. https://mondivago.com/italy/rome) */
export function canonicalizeUrl(pathname: string) {
  return toAbsoluteUrl(normalizePath(pathname));
}
